/** @jsxImportSource react */
import React, { useState, FormEvent, FC } from 'react';
import { useMutation } from '@tanstack/react-query';
import { toast } from '../components/ui/Toast';
import { Link } from 'react-router-dom';
import { UserPlus, Info, Globe, Loader2, Mail, Lock, Phone, ArrowRight, User as UserIcon, MapPin } from 'lucide-react';

import { api } from '../services/api';
import { COUNTRY_CODES } from '../constants';
import { User, UserRole } from '../types';
import { useTranslation } from '../i18n';

const disposableEmailDomains = [
  '10minutemail.com', 'temp-mail.org', 'guerrillamail.com', 'mailinator.com', 
  'getnada.com', 'throwawaymail.com', 'yopmail.com', 'maildrop.cc', 
  'inboxalias.com', 'spamgourmet.com', 'sharklasers.com', 'fakemail.net',
  'dispostable.com', 'mytemp.email', 'tempmail.com'
];

const isDisposableEmail = (email: string): boolean => {
  if (!email) return false;
  const domain = email.split('@')[1];
  if (!domain) return false;
  return disposableEmailDomains.includes(domain.toLowerCase());
};

const SignUpPage: FC = () => {
  const { t } = useTranslation();
  
  const [name, setName] = useState('');
  const [city, setCity] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [countryCode, setCountryCode] = useState('+964');
  const [phone, setPhone] = useState('');

  const signupMutation = useMutation({
    mutationFn: (newUser: Partial<User>) => api.signup(newUser),
    onSuccess: () => {
        toast.success(t('signupSuccess'));
        // AuthProvider will handle user state via onAuthStateChanged
        // App.tsx automatically redirects logged in users to /dashboard
    },
    onError: (error: any) => {
        let message = t('signupError');
        if (error.code) {
            switch (error.code) {
                case 'auth/email-already-in-use':
                    message = t('emailInUseError');
                    break;
                case 'auth/invalid-email':
                    message = t('invalidEmailError') || 'Please enter a valid email address.';
                    break;
                case 'auth/weak-password':
                    message = t('weakPasswordError') || 'Password should be at least 6 characters.';
                    break;
                case 'auth/operation-not-allowed':
                    message = t('authNotEnabledError') || 'Email/password sign-up is not enabled. Please contact support.';
                    break;
                case 'auth/too-many-requests':
                    message = t('tooManyRequestsError') || 'Too many requests. Please try again later.';
                    break;
                default:
                    // For other Firebase errors, show the code if available
                    message = error.message || `${t('signupError')} (Code: ${error.code})`;
            }
        } else if (error.message) {
             // Fallback for generic errors
             if (error.message.includes("already in use") || error.message.includes("already exists")) {
                 message = t('emailInUseError');
             } else {
                 message = error.message;
             }
        }
        toast.error(message);
    }
  });

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();

    if (isDisposableEmail(email)) {
        toast.error(t('disposableEmailError'));
        return;
    }

    if (!name || !email || !password || !phone || !city) {
        toast.error(t('fillRequiredFieldsError'));
        return;
    }
    
    const newUser: Partial<User> = {
        name,
        city,
        email,
        password,
        role: UserRole.Client,
        countryCode,
        phone,
    };
    signupMutation.mutate(newUser);
  };

  return (
    <div className="relative min-h-screen w-full bg-white flex items-center justify-center p-4 overflow-hidden">
      {/* Background decorative elements */}
      <div className="absolute -top-24 -left-24 w-96 h-96 bg-primary-500/10 rounded-full mix-blend-multiply filter blur-3xl opacity-50"></div>
      <div className="absolute -bottom-24 -right-24 w-96 h-96 bg-secondary-500/10 rounded-full mix-blend-multiply filter blur-3xl opacity-50"></div>
      
      <div className="relative z-10 w-full max-w-4xl mx-auto grid md:grid-cols-2 shadow-2xl rounded-xl my-12 border border-secondary-100 bg-white overflow-hidden">
        {/* Left panel - decorative with info */}
        <div className="hidden md:flex flex-col items-center justify-center p-12 text-center relative overflow-hidden bg-secondary-50/50">
          <div className="absolute -top-20 -left-20 w-48 h-48 bg-primary-500/10 rounded-full animate-pulse"></div>
          <div className="absolute bottom-4 -right-12 w-48 h-48 bg-primary-300/10 rounded-full animate-pulse"></div>
          <div className="relative z-10">
            <div className="flex items-center justify-center mb-6">
              <div className="h-16 w-16 bg-primary-500 rounded-lg flex items-center justify-center text-white">
                <UserPlus size={32} />
              </div>
            </div>
            <h2 className="text-3xl font-bold mt-4 text-secondary-900">
              {t('joinUsToday')}
            </h2>
            <p className="mt-2 text-secondary-600">
              {t('createAnAccountToStartShipping')}
            </p>
            <div className="mt-8 text-start">
              <div className={`flex items-center space-x-2 mb-4`}>
                <div className="h-10 w-10 bg-primary-500/10 rounded-full flex items-center justify-center text-primary-500 flex-shrink-0">
                  <ArrowRight className="rtl:rotate-180" />
                </div>
                <span className="text-secondary-700">{t('easyAndSecureSignup')}</span>
              </div>
              <div className={`flex items-center space-x-2 mb-4`}>
                <div className="h-10 w-10 bg-primary-500/10 rounded-full flex items-center justify-center text-primary-500 flex-shrink-0">
                  <Info />
                </div>
                <span className="text-secondary-700">{t('trackYourShipments')}</span>
              </div>
              <div className={`flex items-center space-x-2`}>
                <div className="h-10 w-10 bg-primary-500/10 rounded-full flex items-center justify-center text-primary-500 flex-shrink-0">
                  <Globe />
                </div>
                <span className="text-secondary-700">{t('accessGlobalShipping')}</span>
              </div>
            </div>
          </div>
        </div>
        
        {/* Right panel - signup form */}
        <div className="bg-white p-8 sm:p-12">
          <div className="md:hidden flex justify-center mb-6">
            <div className="h-16 w-16 bg-primary-500 rounded-lg flex items-center justify-center text-white">
              <UserPlus size={32} />
            </div>
          </div>
          <h2 className="text-3xl font-bold text-secondary-900 text-center mb-2">
            {t('createAccountTitle')}
          </h2>
          <p className="text-center text-secondary-500 mb-8">
            {t('joinOurGlobalShippingNetwork')}
          </p>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-secondary-700 text-sm font-medium mb-2" htmlFor="name">{t('fullNameLabel')}</label>
              <div className="relative">
                <div className={`absolute inset-y-0 start-3 flex items-center pointer-events-none`}>
                  <UserIcon size={18} className="text-secondary-400" />
                </div>
                <input id="name" type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder={t('fullNamePlaceholder')} required className={`w-full py-3 ps-10 pe-4 rounded-lg border border-secondary-300 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200`} />
              </div>
            </div>
            <div>
              <label className="block text-secondary-700 text-sm font-medium mb-2" htmlFor="city">{t('cityLabel')}</label>
              <div className="relative">
              <div className={`absolute inset-y-0 start-3 flex items-center pointer-events-none`}>
                  <MapPin size={18} className="text-secondary-400" />
              </div>
              <input id="city" type="text" value={city} onChange={(e) => setCity(e.target.value)} placeholder={t('cityPlaceholder')} required className={`w-full py-3 ps-10 pe-4 rounded-lg border border-secondary-300 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200`} />
              </div>
            </div>
            <div>
              <label className="block text-secondary-700 text-sm font-medium mb-2" htmlFor="email">{t('email_label')}</label>
              <div className="relative">
                <div className={`absolute inset-y-0 start-3 flex items-center pointer-events-none`}>
                  <Mail size={18} className="text-secondary-400" />
                </div>
                <input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder={t('emailPlaceholder')} required className={`w-full py-3 ps-10 pe-4 rounded-lg border border-secondary-300 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200`} />
              </div>
            </div>
             <div>
                <label className="block text-secondary-700 text-sm font-medium mb-2" htmlFor="phone">{t('phoneNumberLabel')}</label>
                <div className="flex">
                    <select id="countryCode" name="countryCode" value={countryCode} onChange={(e) => setCountryCode(e.target.value)} className="appearance-none rounded-s-lg rtl:rounded-s-none rtl:rounded-e-lg border border-e-0 rtl:border-e-auto rtl:border-s-0 border-secondary-300 bg-secondary-100 px-3 py-3 text-secondary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors">
                        {COUNTRY_CODES.map(c => <option key={c.code} value={c.code}>{c.code}</option>)}
                    </select>
                    <div className="relative flex-1">
                        <div className={`absolute inset-y-0 start-3 flex items-center pointer-events-none`}>
                            <Phone size={18} className="text-secondary-400" />
                        </div>
                        <input id="phone" type="tel" value={phone} onChange={(e) => setPhone(e.target.value.replace(/[^0-9]/g, ''))} placeholder={t('phonePlaceholder')} required className={`w-full rounded-e-lg rtl:rounded-e-none rtl:rounded-s-lg border border-secondary-300 py-3 ps-10 pe-4 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200`} />
                    </div>
                </div>
            </div>
            <div>
              <label className="block text-secondary-700 text-sm font-medium mb-2" htmlFor="password">{t('password_label')}</label>
              <div className="relative">
                <div className={`absolute inset-y-0 start-3 flex items-center pointer-events-none`}>
                  <Lock size={18} className="text-secondary-400" />
                </div>
                <input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder={t('passwordPlaceholder')} required className={`w-full py-3 ps-10 pe-4 rounded-lg border border-secondary-300 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200`} />
              </div>
            </div>
            <button
              type="submit"
              disabled={signupMutation.isPending}
              className={`w-full flex items-center justify-center bg-primary-500 text-white py-3 px-4 rounded-lg font-medium mt-4 transition-all duration-300 hover:bg-primary-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 ${signupMutation.isPending ? 'opacity-70 cursor-not-allowed' : ''}`}
            >
              {signupMutation.isPending ? (
                <Loader2 className="animate-spin h-5 w-5 me-2" />
              ) : (
                <>
                  {t('createAccount')}
                  <ArrowRight
                    className={`ms-2 h-5 w-5 rtl:rotate-180`}
                  />
                </>
              )}
            </button>
          </form>
          <div className="text-center mt-8">
            <p className="text-sm text-secondary-600">
              {t('alreadyHaveAnAccount')}{' '}
              <Link
                to="/login"
                className="font-medium text-primary-500 hover:text-primary-600 transition-colors"
              >
                {t('signIn')}
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SignUpPage;